/**
 * Responder Signatures Component
 * Allows multiple responders to digitally sign drill reports
 */
import { Eraser, Check, UserCircle } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useRef, useState, useEffect } from 'react'
import SignatureCanvas from 'react-signature-canvas'

import { Badge } from '@/components/elements/badge'
import { Button } from '@/components/elements/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'

export function ResponderSignatures({ responders = [], onSignaturesChange }) {
  const [signatures, setSignatures] = useState({})
  const sigRefs = useRef({})

  // Initialize signatures from responders prop
  useEffect(() => {
    const initialSignatures = {}
    responders.forEach((responder) => {
      if (responder.signature) {
        initialSignatures[responder.user_id] = responder.signature
      }
    })
    setSignatures(initialSignatures)
  }, [responders])

  const handleClear = (userId) => {
    if (sigRefs.current[userId]) {
      sigRefs.current[userId].clear()
    }
    const updatedSignatures = { ...signatures }
    delete updatedSignatures[userId]
    setSignatures(updatedSignatures)
    onSignaturesChange?.(updatedSignatures)
  }

  const handleEnd = (userId) => {
    const canvas = sigRefs.current[userId]
    if (canvas && !canvas.isEmpty()) {
      const dataUrl = canvas.toDataURL('image/png')
      const updatedSignatures = { ...signatures, [userId]: dataUrl }
      setSignatures(updatedSignatures)
      onSignaturesChange?.(updatedSignatures)
    }
  }

  if (responders.length === 0) {
    return null
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Responder Signatures</CardTitle>
        <p className="text-muted-foreground text-sm">
          Each responder should sign below to confirm their participation
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {responders.map((responder) => {
          const userId = responder.user_id || responder.userId
          const userName = responder.user_name || responder.userName
          const isSigned = !!signatures[userId]

          return (
            <div key={userId} className="space-y-3">
              {/* Responder Info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <UserCircle className="text-muted-foreground h-5 w-5" />
                  <span className="font-medium">{userName}</span>
                  {responder.roles && responder.roles.length > 0 && (
                    <div className="flex gap-1">
                      {responder.roles.map((role) => (
                        <Badge key={role} variant="outline" className="text-xs">
                          {role}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                {isSigned && (
                  <span className="flex items-center gap-1 text-sm font-normal text-green-600">
                    <Check className="h-4 w-4" />
                    Signed
                  </span>
                )}
              </div>

              {/* Signature Pad */}
              <div className="border-input rounded-lg border-[1.5px] bg-white">
                <SignatureCanvas
                  ref={(ref) => {
                    if (ref) sigRefs.current[userId] = ref
                  }}
                  canvasProps={{
                    className: 'w-full h-32 touch-none',
                  }}
                  onEnd={() => handleEnd(userId)}
                />
              </div>

              {/* Clear Button */}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => handleClear(userId)}
              >
                <Eraser className="mr-2 h-4 w-4" />
                Clear Signature
              </Button>
            </div>
          )
        })}

        {/* Summary */}
        <div className="border-t pt-4">
          <p className="text-muted-foreground text-sm">
            {Object.keys(signatures).length} of {responders.length} responder
            {responders.length !== 1 ? 's' : ''} signed
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

ResponderSignatures.propTypes = {
  responders: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      user_name: PropTypes.string,
      userName: PropTypes.string,
      roles: PropTypes.arrayOf(PropTypes.string),
      signature: PropTypes.string,
    }),
  ),
  onSignaturesChange: PropTypes.func.isRequired,
}
